export const typescriptQuestions = [
  {
    id: 1,
    question: "What are the pros and cons of using TypeScript?",
    parts: [
      {
        type: "text",
        content: `**TypeScript Pros & Cons**

**Pros**
1. 🛡️ Type safety: catch errors at compile time
2. 🧠 IDE support: IntelliSense, refactors, docs
3. 📝 Self-documenting via type annotations
4. 🔧 Easier refactoring: compiler flags breaking changes
5. 🤝 Team collaboration: clear contracts between modules
6. 🚀 Modern JS features compiled to targets
7. 🌍 Ecosystem: widespread type defs (\`@types/*\`)

**Cons**
1. 📚 Learning curve (generics, interfaces)
2. ✍️ More verbosity from annotations
3. 🏗️ Build step required (transpile)
4. 🧩 Imperfect types: \`any\`, complex types, missing third-party types
5. ⚙️ Config overhead (\`tsconfig.json\`)
6. 🐣 Overkill for very small/prototype projects

**Guidance**
- Use for large codebases, teams, long-term maintenance
- Skip for quick prototypes, very small scripts`
      },
      {
        type: "code",
        language: "ts",
        content: "function addUser(name: string, age: number): User {\n  // Type annotations make expectations clear\n  return { name, age } as User;\n}"
      }
    ]
  },
  {
    id: 2,
    question: "What is the difference between type and interface in TypeScript?",
    parts: [
      {
        type: "text",
        content: `**type vs interface** — both define shapes but differ in capabilities.

**Interface**
- 🔗 Extensible via \`extends\`
- ➕ Supports declaration merging
- 🧱 Primarily for object shapes and class contracts

**Type**
- 🧰 More flexible: primitives, unions, intersections, tuples
- 🚫 No declaration merging (use intersections instead)

**Guidance**
- Interface: public object shapes/APIs
- Type: unions, primitives, tuples, complex/utility types`
      },
      {
        type: "code",
        language: "ts",
        content: "// Interface\ninterface User { name: string; age: number; }\ninterface Admin extends User { role: string; }\n\n// Declaration merging\ninterface Window { myProp: string; }\ninterface Window { myOtherProp: number; }\n\n// Type\ntype UserT = { name: string; age: number; };\ntype AdminT = UserT & { role: string; };\n\n// Unions / primitives / tuples\ntype Status = 'pending' | 'approved' | 'rejected';\ntype ID = string | number;\ntype Point = [number, number];"
      }
    ]
  },
  {
    id: 3,
    question: "Can TypeScript run directly in the browser?",
    parts: [
      {
        type: "text",
        content: `**TypeScript in the Browser**

**Short Answer: No** ❌
- Browsers only understand JavaScript, not TypeScript
- TypeScript must be **transpiled** to JavaScript first

**The Workflow**
1. 📝 Write TypeScript code (\`.ts\` files)
2. 🔄 Transpile using \`tsc\` (TypeScript compiler)
3. 📦 Output JavaScript (\`.js\` files)
4. 🌐 Browser runs the JavaScript

**Build Tools**
- **tsc**: Official TypeScript compiler
- **Webpack/Vite**: Bundlers with TS support
- **Babel**: Alternative transpiler
- **esbuild/swc**: Fast modern transpilers

**Development**
- ⚡ Dev servers handle transpilation automatically
- 🔥 Hot reload during development
- 📊 Source maps link JS back to TS for debugging

**TypeScript is a development-time tool**, not runtime.`
      },
      {
        type: "code",
        language: "ts",
        content: "// TypeScript code (example.ts)\nfunction greet(name: string): string {\n  return 'Hello, ' + name + '!';\n}\n\n// After transpilation (example.js)\nfunction greet(name) {\n  return 'Hello, ' + name + '!';\n}\n\n// tsconfig.json controls output\n{\n  \"compilerOptions\": {\n    \"target\": \"ES2020\",\n    \"module\": \"ESNext\",\n    \"outDir\": \"./dist\"\n  }\n}"
      }
    ]
  }
];
